// Anatomy of a curled-up sleeping dog, defined as a set of overlapping ellipses.
// From these ellipses we generate the fine-line body outline AND the individual
// fur strands. Every strand knows its anchor point, base outward angle, length
// and a random phase used to animate it in a wind-like manner.

// viewBox: 1000 x 640
export const VIEW = { w: 1000, h: 640 };

// { cx, cy, rx, ry, rot } -- ellipse in local SVG space.
// The order matters for outline drawing (earlier drawn first, later on top).
export const PARTS = {
  body: { cx: 560, cy: 400, rx: 275, ry: 165, rot: -0.06 },
  hip: { cx: 720, cy: 385, rx: 130, ry: 130, rot: 0 },
  shoulder: { cx: 380, cy: 400, rx: 130, ry: 120, rot: 0.05 },
  head: { cx: 240, cy: 400, rx: 115, ry: 100, rot: -0.08 },
  snout: { cx: 138, cy: 432, rx: 50, ry: 32, rot: -0.15 },
  ear: { cx: 280, cy: 322, rx: 78, ry: 34, rot: -0.42 },
  earAwake: { cx: 260, cy: 285, rx: 32, ry: 78, rot: -0.1 },
  frontPaws: { cx: 205, cy: 490, rx: 60, ry: 24, rot: -0.05 },
  backPaw: { cx: 690, cy: 495, rx: 55, ry: 22, rot: 0.08 },
  tail: { cx: 830, cy: 340, rx: 95, ry: 32, rot: -0.55 },
};

// Rotate a point (x, y) around origin by angle a (radians).
const rot = (x, y, a) => {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
};

// Parametric point on an oriented ellipse at parameter t in [0,1].
// Also returns outward unit normal.
export const ellipsePoint = (e, t) => {
  const theta = t * Math.PI * 2;
  const lx = e.rx * Math.cos(theta);
  const ly = e.ry * Math.sin(theta);
  const [wx, wy] = rot(lx, ly, e.rot);
  const px = e.cx + wx;
  const py = e.cy + wy;
  // Outward normal in local frame is (cos(theta)/rx, sin(theta)/ry) then rotated.
  let nx_l = Math.cos(theta) / e.rx;
  let ny_l = Math.sin(theta) / e.ry;
  const [nx, ny] = rot(nx_l, ny_l, e.rot);
  const mag = Math.hypot(nx, ny) || 1;
  return { x: px, y: py, nx: nx / mag, ny: ny / mag };
};

// Deterministic seeded PRNG so the fur is stable between renders.
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Generate fur strands around an ellipse. `slice` restricts to a t-range, so
// we can skip the underside (where the dog rests on the ground).
const strandsForEllipse = (e, {
  count,
  slice = [0, 1],
  baseLen = 12,
  jitterLen = 6,
  jitterAngle = 0.35,
  offsetIn = 0,
  rand,
  layer = 0,
}) => {
  const out = [];
  const [t0, t1] = slice;
  for (let i = 0; i < count; i++) {
    const t = t0 + ((i + rand() * 0.7) / count) * (t1 - t0);
    const p = ellipsePoint(e, t);
    // Push anchor slightly inward so the hair root sits inside the outline
    const ax = p.x - p.nx * offsetIn;
    const ay = p.y - p.ny * offsetIn;
    const baseAngle = Math.atan2(p.ny, p.nx) + (rand() - 0.5) * jitterAngle;
    const length = baseLen + (rand() - 0.5) * 2 * jitterLen;
    const phase = rand() * Math.PI * 2;
    // How much a hair is affected by wind (tips of ears, tail bushier)
    const responsiveness = 0.6 + rand() * 0.8;
    out.push({
      x: ax,
      y: ay,
      baseAngle,
      length: Math.max(3, length),
      phase,
      responsiveness,
      layer,
    });
  }
  return out;
};

// Interior "coat" hairs distributed inside the body ellipses. They point
// mostly outward from the body centre.
const interiorStrands = (e, { count, bodyCenter, baseLen = 8, jitterLen = 4, rand, layer = 0 }) => {
  const out = [];
  for (let i = 0; i < count; i++) {
    // Rejection sample points inside the ellipse (in its local frame)
    let lx, ly;
    while (true) {
      lx = (rand() * 2 - 1);
      ly = (rand() * 2 - 1);
      if (lx * lx + ly * ly <= 1) break;
    }
    lx *= e.rx * 0.9;
    ly *= e.ry * 0.9;
    const [wx, wy] = rot(lx, ly, e.rot);
    const x = e.cx + wx;
    const y = e.cy + wy;
    const dx = x - bodyCenter.x;
    const dy = y - bodyCenter.y;
    const baseAngle = Math.atan2(dy, dx) + (rand() - 0.5) * 0.6;
    const length = baseLen + (rand() - 0.5) * 2 * jitterLen;
    out.push({
      x,
      y,
      baseAngle,
      length: Math.max(3, length),
      phase: rand() * Math.PI * 2,
      responsiveness: 0.4 + rand() * 0.7,
      layer,
    });
  }
  return out;
};

export const buildFur = (seed = 42) => {
  const rand = mulberry32(seed);
  const bodyCenter = { x: PARTS.body.cx, y: PARTS.body.cy };

  const strands = [];

  // Perimeter fur -- upper body sweep (skip underside where dog touches ground)
  strands.push(
    ...strandsForEllipse(PARTS.body, {
      count: 180,
      slice: [-0.05, 0.55], // top of body, curled back
      baseLen: 16,
      jitterLen: 7,
      jitterAngle: 0.45,
      offsetIn: 2,
      rand,
      layer: 2,
    })
  );
  // Small sweep along the tail-side (right)
  strands.push(
    ...strandsForEllipse(PARTS.hip, {
      count: 90,
      slice: [-0.1, 0.55],
      baseLen: 18,
      jitterLen: 8,
      jitterAngle: 0.5,
      offsetIn: 1,
      rand,
      layer: 2,
    })
  );
  strands.push(
    ...strandsForEllipse(PARTS.shoulder, {
      count: 80,
      slice: [0.05, 0.55],
      baseLen: 14,
      jitterLen: 6,
      jitterAngle: 0.4,
      offsetIn: 1,
      rand,
      layer: 2,
    })
  );
  // Head fur (top + sides)
  strands.push(
    ...strandsForEllipse(PARTS.head, {
      count: 70,
      slice: [0.05, 0.65],
      baseLen: 12,
      jitterLen: 5,
      jitterAngle: 0.5,
      offsetIn: 1,
      rand,
      layer: 3,
    })
  );
  // Snout (soft)
  strands.push(
    ...strandsForEllipse(PARTS.snout, {
      count: 34,
      slice: [0.05, 0.65],
      baseLen: 6,
      jitterLen: 2,
      jitterAngle: 0.4,
      offsetIn: 0.5,
      rand,
      layer: 3,
    })
  );
  // Ear (fluffy tip, responsive)
  strands.push(
    ...strandsForEllipse(PARTS.ear, {
      count: 60,
      slice: [-0.05, 0.55],
      baseLen: 14,
      jitterLen: 6,
      jitterAngle: 0.5,
      offsetIn: 1,
      rand,
      layer: 4,
    })
  );
  // Tail (bushy)
  strands.push(
    ...strandsForEllipse(PARTS.tail, {
      count: 90,
      slice: [-0.1, 1.1],
      baseLen: 20,
      jitterLen: 9,
      jitterAngle: 0.7,
      offsetIn: 1,
      rand,
      layer: 4,
    })
  );
  // Front paws small fur
  strands.push(
    ...strandsForEllipse(PARTS.frontPaws, {
      count: 30,
      slice: [-0.05, 0.55],
      baseLen: 8,
      jitterLen: 3,
      jitterAngle: 0.4,
      offsetIn: 0.5,
      rand,
      layer: 3,
    })
  );
  strands.push(
    ...strandsForEllipse(PARTS.backPaw, {
      count: 26,
      slice: [-0.05, 0.55],
      baseLen: 8,
      jitterLen: 3,
      jitterAngle: 0.4,
      offsetIn: 0.5,
      rand,
      layer: 3,
    })
  );

  // Interior coat (adds density)
  strands.push(
    ...interiorStrands(PARTS.body, { count: 160, bodyCenter, baseLen: 10, jitterLen: 4, rand, layer: 1 })
  );
  strands.push(
    ...interiorStrands(PARTS.hip, { count: 80, bodyCenter, baseLen: 12, jitterLen: 5, rand, layer: 1 })
  );
  strands.push(
    ...interiorStrands(PARTS.shoulder, { count: 60, bodyCenter, baseLen: 10, jitterLen: 4, rand, layer: 1 })
  );
  strands.push(
    ...interiorStrands(PARTS.head, { count: 55, bodyCenter: { x: PARTS.head.cx, y: PARTS.head.cy }, baseLen: 8, jitterLen: 3, rand, layer: 1 })
  );
  strands.push(
    ...interiorStrands(PARTS.tail, { count: 60, bodyCenter: { x: PARTS.tail.cx, y: PARTS.tail.cy }, baseLen: 14, jitterLen: 5, rand, layer: 1 })
  );

  return strands;
};

// Body outline paths (soft, layered), used as the main silhouette.
// We render each ellipse as an SVG ellipse; overlaps blend into a single form.
export const bodyEllipses = [
  PARTS.body,
  PARTS.hip,
  PARTS.shoulder,
  PARTS.head,
  PARTS.snout,
  PARTS.frontPaws,
  PARTS.backPaw,
];
